import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Modal, ModalTitle, ModalBody, ModalFooter } from 'design-system/src/components/Modal';
import Button from 'design-system/src/components/Button/Button';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { client } from '../../api';
import * as Sentry from '@sentry/react';

type RemoveUnmatchedAddressModalProps = {
  /** Called when the modal should close. */
  onClose: () => void;
  /** The address to remove. */
  address: string;
};

const RemoveUnmatchedAddressModal: React.FC<RemoveUnmatchedAddressModalProps> = ({ onClose, address }) => {
  const queryClient = useQueryClient();

  const { mutate: dismissAddress, isPending } = useMutation({
    mutationFn: () => client.post('/settings/offices/unmatched-addresses/dismiss', { addresses: [address] }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unmatched-addresses'] });
      toast.custom(() => <Toast variant="success">Address removed</Toast>);
      onClose();
    },
    onError: (error) => {
      Sentry.captureException(error);
      toast.custom(() => <StandardErrorToast />);
    },
  });

  return (
    <Modal onClose={onClose}>
      <ModalTitle>Remove this unmatched address?</ModalTitle>
      <ModalBody>
        <Typography styledAs="bodyDMSans" weight={700}>
          {address}
        </Typography>
        <Typography styledAs="bodyDMSans">
          Tandem found this address on one or more of your practice's prescriptions, but it hasn't been assigned to an
          office.
        </Typography>
        <Typography styledAs="bodyDMSans">
          If removed, it may be added again if new prescriptions include it.
        </Typography>
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" disabled={isPending} onClick={onClose}>
          Back
        </Button>
        <Button variant="primary" isLoading={isPending} onClick={() => dismissAddress()}>
          Delete address
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default RemoveUnmatchedAddressModal;
